/**
 * Solapa "Reingresos" de Despacho — unidades cuyo egreso se anuló "en
 * tránsito" (salieron del depósito, se canceló la traza y vuelven). Quedan no
 * disponibles hasta que llegan; acá se confirma el reingreso físico, y pasan a
 * disponibles (evento REINGRESO).
 *
 * Las unidades se marcan escaneando su DataMatrix (mismo campo de lectura que
 * el pickeo, con sus correcciones de teclado) o tildándolas a mano. "Confirmar
 * reingreso" pide confirmación y lo hace en el back en una sola operación.
 */

import { useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/lib/notify';
import { playScanSound } from '@/lib/audio';
import { useSessionStore } from '@/stores/session';
import { useConfirmarReingresoMutation, useReingresosPendientes } from '@/api/queries';
import type { ReingresoPendiente } from '@/api/contracts/reingresos';
import { CampoLecturaGs1, type MetaLectura } from '@/components/lector/CampoLecturaGs1';
import { ubicarUnidadReingreso } from './reingreso-lectura';

/** "YYYY-MM-DD" → "DD/MM/YYYY". */
function fmtFecha(value: string | null): string {
  if (!value) return '—';
  const [y, m, d] = value.slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : value;
}

function fmtFechaHora(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
}

export function ReingresosPendientesList() {
  const glnId = useSessionStore((s) => s.ubicacionActivaId);
  const { data: unidades = [], isLoading, error } = useReingresosPendientes(glnId);
  const confirmar = useConfirmarReingresoMutation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [seleccion, setSeleccion] = useState<ReadonlySet<number>>(new Set());
  const [ultimaLectura, setUltimaLectura] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  // Solo cuentan las seleccionadas que siguen en el listado.
  const vigentes = useMemo(
    () => new Set(unidades.filter((u) => seleccion.has(u.stockId)).map((u) => u.stockId)),
    [unidades, seleccion],
  );

  const porRemito = useMemo(() => {
    const map = new Map<string, ReingresoPendiente[]>();
    for (const u of unidades) {
      const k = u.referencia ?? '(sin remito)';
      map.set(k, [...(map.get(k) ?? []), u]);
    }
    return Array.from(map.entries());
  }, [unidades]);

  const alternar = (ids: readonly number[], marcar: boolean) => {
    setSeleccion((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (marcar) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const handleLectura = (raw: string, meta: MetaLectura) => {
    const r = ubicarUnidadReingreso([raw, ...meta.alternativas], unidades);
    if (r.tipo === 'vacia') return;
    if (r.tipo === 'no_encontrada') {
      setUltimaLectura('Esa unidad no está pendiente de reingreso en este GLN.');
      playScanSound('error');
      return;
    }
    const u = unidades.find((x) => x.stockId === r.stockId);
    const yaEstaba = vigentes.has(r.stockId);
    alternar([r.stockId], true);
    setUltimaLectura(
      yaEstaba
        ? `La serie ${u?.serie ?? ''} ya estaba marcada.`
        : `Marcada: serie ${u?.serie ?? ''} (${u?.referencia ?? 'sin remito'}).`,
    );
    playScanSound(yaEstaba ? 'error' : 'success');
  };

  const handleConfirmar = () => {
    if (!glnId || vigentes.size === 0) return;
    confirmar.mutate(
      { glnId, stockIds: Array.from(vigentes) },
      {
        onSuccess: (r) => {
          toast.success(`Reingreso confirmado · ${r.reingresadas} unidad(es) disponibles.`);
          setSeleccion(new Set());
          setUltimaLectura(null);
          setConfirmando(false);
        },
        onError: (err) => {
          toast.error(`No se pudo confirmar el reingreso: ${err.message}`);
          setConfirmando(false);
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base text-everex-azul">Pendientes de reingreso</CardTitle>
        <p className="text-xs text-gray-600">
          Unidades de despachos con el egreso anulado que salieron del depósito y vuelven. No están
          disponibles hasta confirmar que llegaron: escaneá cada unidad o tildala, y confirmá el
          reingreso.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="py-6 text-center text-xs text-gray-500">
            <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Cargando…
          </p>
        ) : error ? (
          <p className="py-6 text-center text-xs text-red-700">
            No se pudo cargar el listado: {error instanceof Error ? error.message : String(error)}
          </p>
        ) : unidades.length === 0 ? (
          <p className="py-6 text-center text-xs text-gray-500">
            No hay unidades pendientes de reingreso en este GLN.
          </p>
        ) : (
          <>
            <CampoLecturaGs1
              inputRef={inputRef}
              onLectura={handleLectura}
              placeholder="Escaneá el DataMatrix de cada unidad que llegó"
            />
            {ultimaLectura && <p className="text-xs text-gray-700">{ultimaLectura}</p>}

            {porRemito.map(([referencia, lista]) => {
              const ids = lista.map((u) => u.stockId);
              const todas = ids.every((id) => vigentes.has(id));
              return (
                <div key={referencia} className="rounded-md border border-gray-200">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 text-xs">
                    <span>
                      <b className="font-mono">{referencia}</b>
                      <span className="ml-2 text-gray-600">
                        · {lista.length} unidad(es) · egreso anulado el{' '}
                        {fmtFechaHora(lista[0]?.anuladoAt ?? null)}
                        {lista[0]?.anuladoPor ? ` por ${lista[0].anuladoPor}` : ''}
                      </span>
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => alternar(ids, !todas)}
                    >
                      {todas ? 'Desmarcar el remito' : 'Marcar todo el remito'}
                    </Button>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="text-[11px] uppercase text-gray-600">
                      <tr>
                        <th className="w-8 px-3 py-1.5" aria-label="Marcada" />
                        <th className="px-3 py-1.5 text-left">SKU</th>
                        <th className="px-3 py-1.5 text-left">GTIN</th>
                        <th className="px-3 py-1.5 text-left">Lote</th>
                        <th className="px-3 py-1.5 text-left">N° de serie</th>
                        <th className="px-3 py-1.5 text-left">Vencimiento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lista.map((u) => (
                        <tr
                          key={u.stockId}
                          className={vigentes.has(u.stockId) ? 'bg-green-50' : 'border-t border-gray-100'}
                        >
                          <td className="px-3 py-1.5">
                            <input
                              type="checkbox"
                              checked={vigentes.has(u.stockId)}
                              onChange={(e) => alternar([u.stockId], e.target.checked)}
                              aria-label={`Marcar la serie ${u.serie ?? ''}`}
                            />
                          </td>
                          <td className="px-3 py-1.5 font-mono">{u.sku ?? '—'}</td>
                          <td className="px-3 py-1.5 font-mono text-gray-600">{u.gtin ?? '—'}</td>
                          <td className="px-3 py-1.5 font-mono">{u.lote ?? '—'}</td>
                          <td className="px-3 py-1.5 font-mono font-semibold">{u.serie ?? '—'}</td>
                          <td className="px-3 py-1.5">{fmtFecha(u.vencimiento)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}

            <div className="flex justify-end">
              <Button
                onClick={() => setConfirmando(true)}
                disabled={vigentes.size === 0 || confirmar.isPending}
                className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
              >
                Confirmar reingreso ({vigentes.size})
              </Button>
            </div>
          </>
        )}
      </CardContent>

      <Dialog open={confirmando} onOpenChange={(o) => !confirmar.isPending && setConfirmando(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar reingreso</DialogTitle>
            <DialogDescription className="text-xs">
              Confirmás que <b>{vigentes.size} unidad(es)</b> llegaron físicamente al depósito. Pasan a
              estar disponibles para otros remitos.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmando(false)}
              disabled={confirmar.isPending}
            >
              Volver
            </Button>
            <Button
              onClick={handleConfirmar}
              disabled={confirmar.isPending}
              className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
            >
              {confirmar.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
